import type { AccessModule } from "@/shared/access/modules";
/**
 * AS ABAS DE `/settings`, UMA VEZ — e com elas os endereços antigos.
 *
 * ── O DEFEITO QUE ESTA PÁGINA CONSERTA ────────────────────────────────────
 *
 * Não era falta de capacidade: o produto tinha tudo. Era falta de LUGAR. As
 * configurações estavam em cinco destinos de primeiro nível (`/account`, `/team`,
 * `/channels`, `/usage`, `/status`), o tema morava num botão do canto da tela de
 * Status, e quem paga pelo workspace dizia, com razão, que não sabia onde
 * configurava o quê. Uma página só, com menu horizontal, é a resposta.
 *
 * ── O CRITÉRIO, PARA A PRÓXIMA PESSOA NÃO MOVER TELA POR GOSTO ────────────
 *
 * Três perguntas, nesta ordem. Só a terceira produz aba.
 *
 * 1. TEM ALGUÉM ESPERANDO DO OUTRO LADO? Um cliente sem resposta, uma proposta
 *    parada, um canal caído às nove da manhã. Então é TRABALHO e fica fora: o
 *    caminho até ele tem de ser um clique do menu, nunca dois cliques e uma aba.
 *
 * 2. A EDIÇÃO ALCANÇA OUTRO TENANT? Então é a INSTALAÇÃO e fica no grupo Dev
 *    (`/platform`, `/prompts`). Uma tela chamada "Configurações" dentro do
 *    workspace de alguém promete que o que se edita ali é DELE — e "Instruções do
 *    sistema" alcança todos os workspaces de uma vez. Essa promessa quebrada
 *    custaria caro exatamente uma vez.
 *
 * 3. SOBRA O QUE É SOBRE ESTA CASA E SOBRE VOCÊ. O que você decide e o sistema
 *    obedece (preferências, marca, empresa, assinatura, papéis, senha, tema) e o
 *    que o sistema decidiu e presta contas depois (consumo, dependências).
 *    Ninguém está esperando por nenhum dos dois, e os dois respondem "como está a
 *    minha casa". Estes viram aba.
 *
 * A EXCEÇÃO, declarada e única: uma tela pode responder "sim" à pergunta 1 e
 * ainda assim ter aba, quando a MESMA tela responde a duas perguntas em horas
 * diferentes do dia. Canais é a única — conectar um número é configuração,
 * descobrir às nove que ele caiu é trabalho. Ela ganha aba E continua no menu, e
 * é o MESMO componente nos dois lugares (`ChannelsPage` com `embedded`), pelo
 * motivo que o `PublicFormPage` já documenta: dois arquivos quase iguais divergem.
 *
 * ── POR QUE O ENDEREÇO ANTIGO MORA NESTA MESMA TABELA ─────────────────────
 *
 * `from` é lido pelo `routes.ts` para gerar os redirecionamentos. Escritos à mão
 * lá, um deles apontaria um dia para uma aba que mudou de nome, e o sintoma seria
 * um link salvo que abre a página na aba errada — coisa que ninguém reporta e
 * ninguém liga a um commit. Aqui a aba e o endereço antigo dela são a mesma linha.
 */
export interface SettingsTab {
	/** O que vai no endereço: `/settings?aba=<value>`. */
	readonly value: string;
	/**
	 * A CHAVE do rótulo, e não o rótulo.
	 *
	 * Uma tabela de dados não pode carregar frase: quem sabe em que idioma o
	 * painel está é a hora de desenhar, não a hora de declarar. As três chaves
	 * apontam para `features/workspace/i18n/settings.ts`, e as duas superfícies
	 * que as leem — a faixa de abas e o ⌘K — resolvem cada uma na sua montagem.
	 */
	readonly labelKey: string;
	/**
	 * O que a aba responde, curto.
	 *
	 * Desenhado como subtítulo da página E à direita do rótulo na paleta, onde o
	 * espaço é uma coluna `shrink-0` de fonte mono — por isso curto, e por isso as
	 * palavras com que se PROCURA estão em `terms` e não aqui.
	 */
	readonly hintKey: string;
	/**
	 * A chave das palavras que levam a esta aba no ⌘K, e que a tela não desenha.
	 *
	 * Existe porque a página única só é melhor do que cinco destinos se "senha" e
	 * "fuso horário" continuarem achando o controle pelo NOME dele. Sem isto, a
	 * mudança teria trocado cinco lugares óbvios por um esconderijo com seis
	 * gavetas.
	 */
	readonly termsKey: string;
	/** Abaixo deste papel a aba não é desenhada — e o servidor recusaria de todo jeito. */
	readonly minRole?: "admin";
	/** Os endereços de primeiro nível que passam a cair nesta aba. */
	readonly from: readonly string[];
}

/**
 * `as const satisfies` e não uma anotação de tipo: com a anotação, `value` vira
 * `string` e a página perde a única garantia que importa aqui — o `Record` de
 * painéis dela é indexado por este union, então uma aba acrescentada nesta tabela
 * e esquecida lá não compila. Uma aba que abre vazia é o tipo de defeito que
 * ninguém vê em revisão e todo mundo vê em produção.
 */
export const SETTINGS_TABS = [
	{
		value: "workspace",
		labelKey: "tabs.workspace.label",
		hintKey: "tabs.workspace.hint",
		termsKey: "tabs.workspace.terms",
		from: ["/account"],
	},
	{
		value: "cadastros",
		labelKey: "tabs.cadastros.label",
		hintKey: "tabs.cadastros.hint",
		termsKey: "tabs.cadastros.terms",
		from: [],
	},
	{
		value: "conta",
		labelKey: "tabs.conta.label",
		hintKey: "tabs.conta.hint",
		termsKey: "tabs.conta.terms",
		// Sem `from`: nunca houve um endereço só para você. Era metade da tela de
		// Conta, ao lado das preferências do workspace — dois escopos numa tela só,
		// que é o que fazia "onde troco minha senha" ser uma pergunta.
		from: [],
	},
	{
		value: "equipe",
		labelKey: "tabs.equipe.label",
		hintKey: "tabs.equipe.hint",
		termsKey: "tabs.equipe.terms",
		minRole: "admin",
		from: ["/team"],
	},
	{
		/**
		 * ACESSOS — quem alcança o quê, pessoas e agentes juntos.
		 *
		 * Aba própria e não uma gaveta dentro de Equipe: Equipe é sobre quem está
		 * aqui, e metade desta tela é sobre AGENTES, que não estão na equipa. Era
		 * também a pergunta mais espalhada do produto — o papel em Equipe, as
		 * restrições em Canais e em Funis, a lista de entidades na tela do agente —
		 * e nenhuma das quatro sabia da outra.
		 */
		value: "acessos",
		labelKey: "tabs.acessos.label",
		hintKey: "tabs.acessos.hint",
		termsKey: "tabs.acessos.terms",
		minRole: "admin",
		// Sem `from`: nunca houve endereço para isto — a pergunta nascia repartida.
		from: [],
	},
	{
		/**
		 * SEGREDOS — as chaves de API e tokens que funções e `http_request` usam.
		 *
		 * Só administrador: o servidor recusa (403) a qualquer outro papel, e o valor
		 * nunca volta do servidor — a tela mostra só a prévia mascarada.
		 */
		value: "segredos",
		labelKey: "tabs.segredos.label",
		hintKey: "tabs.segredos.hint",
		termsKey: "tabs.segredos.terms",
		minRole: "admin",
		from: [],
	},
	{
		value: "webhooks",
		labelKey: "tabs.webhooks.label",
		hintKey: "tabs.webhooks.hint",
		termsKey: "tabs.webhooks.terms",
		minRole: "admin",
		from: [],
	},
	{
		/**
		 * A REDE — a matriz e as unidades dela (plano Escala).
		 *
		 * A aba é desenhada para todo administrador, e NÃO só para quem já tem
		 * rede: o painel dentro dela é que responde em qual dos quatro estados
		 * este workspace está — matriz, unidade, pode criar, ou o plano não vende.
		 * Esconder a aba de quem não tem o plano seria esconder a existência do
		 * produto de quem exatamente devia descobri-lo, e obrigaria esta tabela
		 * (que também alimenta o ⌘K) a saber o plano — uma segunda pergunta a
		 * manter sincronizada com o servidor, que já responde `available`.
		 */
		value: "organizacao",
		labelKey: "tabs.organizacao.label",
		hintKey: "tabs.organizacao.hint",
		termsKey: "tabs.organizacao.terms",
		minRole: "admin",
		// Sem `from`: nunca houve endereço para isto — a rede nasce nesta aba.
		from: [],
	},
	{
		value: "canais",
		labelKey: "tabs.canais.label",
		hintKey: "tabs.canais.hint",
		termsKey: "tabs.canais.terms",
		// Sem `from`: `/channels` CONTINUA no menu. É a exceção do critério lá em cima.
		from: [],
	},
	{
		value: "consumo",
		labelKey: "tabs.consumo.label",
		hintKey: "tabs.consumo.hint",
		termsKey: "tabs.consumo.terms",
		from: ["/usage"],
	},
	{
		value: "sistema",
		labelKey: "tabs.sistema.label",
		hintKey: "tabs.sistema.hint",
		termsKey: "tabs.sistema.terms",
		from: ["/status"],
	},
] as const satisfies readonly SettingsTab[];

/** Uma linha da tabela acima, com o `value` ainda literal. */
export type SettingsTabEntry = (typeof SETTINGS_TABS)[number];

/** Os sete nomes que podem estar em `?aba=`. */
export type SettingsTabValue = SettingsTabEntry["value"];

/** O que decide se uma aba é desenhada. O mesmo par que o menu lateral usa. */
export interface SettingsAccess {
	readonly disabledModules?: readonly AccessModule[];
	readonly isAdmin: boolean;
}

/**
 * As abas que ESTA sessão vê.
 *
 * Esconder não é a tranca — o servidor recusa `PUT /v3/workspace/team/:id` a um
 * `member` com ou sem esta função. Isto evita desenhar uma aba cujo conteúdo
 * seria uma lista vazia e uma sequência de 403, que se lê como defeito e não como
 * "não é para você".
 */
export function visibleTabs(access: SettingsAccess): SettingsTabEntry[] {
	return SETTINGS_TABS.filter(
		(tab) => tab.value === "conta" || !access.disabledModules?.includes("workspace"),
	)
		.filter((tab) => tab.value !== "webhooks" || !access.disabledModules?.includes("automation"))
		.filter((tab) => !("minRole" in tab) || access.isAdmin);
}

/**
 * Qual aba o endereço pediu — e a que se abre quando ele não pediu nenhuma.
 *
 * Nunca devolve nada: `?aba=` inventado, ausente ou de uma aba que esta sessão
 * não vê caem todos na primeira visível. Um `member` que recebe de um admin o
 * link de `?aba=equipe` chega numa página que funciona, e não numa em branco.
 */
export function resolveTab(wanted: unknown, access: SettingsAccess): SettingsTabEntry {
	const tabs = visibleTabs(access);
	const found = typeof wanted === "string" ? tabs.find((tab) => tab.value === wanted) : undefined;
	// `tabs[0]` sempre existe: só uma aba declara `minRole`, e as outras cinco não.
	return found ?? (tabs[0] as SettingsTabEntry);
}

/**
 * O endereço colável de uma aba.
 *
 * Uma função porque a grafia do parâmetro é UMA: a página, os redirecionamentos e
 * a paleta escrevem `?aba=` pelo mesmo caminho, e o dia em que ele virar segmento
 * de rota há um lugar só para mudar.
 */
export function tabAddress(value: SettingsTabValue): string {
	return `/settings?aba=${value}`;
}
